import { useState } from "react";
import { useUser, SignInButton, SignedIn, SignedOut } from "@clerk/clerk-react";

const C = {
  bg:         '#020617',
  surface:    'rgba(255,255,255,0.05)',
  surface2:   'rgba(255,255,255,0.08)',
  border:     'rgba(255,255,255,0.10)',
  borderHov:  'rgba(255,255,255,0.20)',
  accent:     '#60A5FA',
  accentDim:  '#3B82F6',
  accentGlow: 'rgba(96,165,250,0.15)',
  accentGlow2:'rgba(96,165,250,0.08)',
  gold:       '#F0A500',
  goldGlow:   'rgba(240,165,0,0.12)',
  green:      '#34D399',
  greenGlow:  'rgba(52,211,153,0.12)',
  red:        '#F87171',
  purple:     '#A78BFA',
  text:       '#FFFFFF',
  textMuted:  '#94A3B8',
  textFaint:  '#475569',
};

const F = {
  display: "'Playfair Display', Georgia, serif",
  body:    "'DM Sans', 'Segoe UI', sans-serif",
};

const CATEGORIES = [
  { id: "all",         label: "All Topics",      icon: "🌐", color: C.accent },
  { id: "ai",          label: "AI & Automation",  icon: "🤖", color: C.accent },
  { id: "nocode",      label: "No-Code",          icon: "⚡", color: C.accentDim },
  { id: "video",       label: "Video & Content",  icon: "🎬", color: C.red },
  { id: "solopreneur", label: "Solopreneur",       icon: "🚀", color: C.gold },
  { id: "design",      label: "Design",           icon: "🎨", color: C.purple },
  { id: "wins",        label: "Wins & Progress",  icon: "🏆", color: C.green },
  { id: "help",        label: "Get Help",         icon: "🙋", color: "#FF9800" },
];

const INITIAL_POSTS = [
  {
    id: 1, category: "ai", title: "How I automated my entire client onboarding with ChatGPT Agents",
    body: "After completing the ChatGPT Agents course I built a system that handles intake forms, sends personalized welcome emails, and schedules the first call — all automatically. Sharing my full workflow here. AMA!",
    author: "Sarah K.", avatar: "SK", time: "2 hours ago", upvotes: 47, replies: 23,
    tags: ["chatgpt", "automation", "client-work"], pinned: true, solved: false,
  },
  {
    id: 2, category: "wins", title: "Just hit $5K this month selling AI templates 🎉",
    body: "6 weeks ago I had zero online income. Today I crossed $5K selling prompt packs and SOP templates on the SkillBite marketplace. Here's exactly what I did and what sold best.",
    author: "Marcus L.", avatar: "ML", time: "4 hours ago", upvotes: 89, replies: 41,
    tags: ["milestone", "templates", "income"], pinned: true, solved: false,
  },
  {
    id: 3, category: "help", title: "Which AI tool is best for WhatsApp automation in Nigeria?",
    body: "I've been looking at ManyChat, Wati, and Respond.io but I'm not sure which works best for Nigerian businesses. Has anyone tested these? I want to start selling automation packages.",
    author: "Tunde O.", avatar: "TO", time: "5 hours ago", upvotes: 12, replies: 18,
    tags: ["whatsapp", "nigeria", "tools"], pinned: false, solved: true,
  },
  {
    id: 4, category: "nocode", title: "Built my first app with no code in 3 days — here's how",
    body: "Used the No-Code App Creation course and built a simple invoice generator for freelancers. Sharing the full process including which tools I used and where I got stuck.",
    author: "Ana C.", avatar: "AC", time: "7 hours ago", upvotes: 34, replies: 15,
    tags: ["nocode", "app", "tutorial"], pinned: false, solved: false,
  },
  {
    id: 5, category: "video", title: "My TikTok went from 200 to 8K followers using AI scripts",
    body: "I've been using the AI video script templates from the marketplace and the growth has been insane. The key insight I missed before: specificity beats broad topics every single time.",
    author: "Maya C.", avatar: "MC", time: "9 hours ago", upvotes: 56, replies: 29,
    tags: ["tiktok", "growth", "content"], pinned: false, solved: false,
  },
  {
    id: 6, category: "ai", title: "Question: How do you handle hallucinations in client-facing AI agents?",
    body: "I'm building an AI customer service agent for a client and I'm worried about it giving wrong information. What guardrails do you all use? Any specific prompting techniques that help?",
    author: "James W.", avatar: "JW", time: "12 hours ago", upvotes: 21, replies: 14,
    tags: ["agents", "prompting", "reliability"], pinned: false, solved: true,
  },
  {
    id: 7, category: "solopreneur", title: "30-day recap: Started a WhatsApp chatbot agency from scratch",
    body: "I'm sharing my full month-by-month breakdown of building and selling WhatsApp automation to local businesses in Lagos. Revenue, clients, what I charged, and what flopped.",
    author: "Emeka D.", avatar: "ED", time: "1 day ago", upvotes: 73, replies: 37,
    tags: ["agency", "whatsapp", "nigeria"], pinned: false, solved: false,
  },
  {
    id: 8, category: "design", title: "AI graphic design workflow that saved me 10 hours/week",
    body: "I now use a combination of Midjourney, Adobe Firefly, and Canva AI to deliver client branding projects 3x faster. Sharing my exact prompt library and workflow.",
    author: "Priya S.", avatar: "PS", time: "1 day ago", upvotes: 38, replies: 19,
    tags: ["design", "workflow", "ai-tools"], pinned: false, solved: false,
  },
  {
    id: 9, category: "help", title: "Stuck on Module 3 of ChatGPT Agents course — API key issue",
    body: "I keep getting a 401 error when trying to connect my OpenAI API key in the automation workflow. I've checked the key 3 times and it's correct. Anyone else had this?",
    author: "David O.", avatar: "DO", time: "2 days ago", upvotes: 5, replies: 8,
    tags: ["technical", "api", "help"], pinned: false, solved: true,
  },
  {
    id: 10, category: "wins", title: "From zero coding skills to building AI agents in 30 days",
    body: "I was a marketing manager with zero technical background. After 30 days on SkillBite I built my first AI agent for lead qualification. The learning curve was steep but doable. Here's my honest review.",
    author: "Lisa P.", avatar: "LP", time: "2 days ago", upvotes: 91, replies: 44,
    tags: ["beginner", "journey", "agents"], pinned: false, solved: false,
  },
];

function Avatar({ initials, size = 36, color = C.accent }) {
  const colors = [C.accent, C.gold, C.green, C.purple, C.red, "#FF9800"];
  const bg = colors[initials.charCodeAt(0) % colors.length];
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: bg + "30", border: `1.5px solid ${bg}50`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.36, fontWeight: 700, color: bg, flexShrink: 0, fontFamily: F.body }}>
      {initials}
    </div>
  );
}

function PostCard({ post, onOpen, onUpvote }) {
  const cat = CATEGORIES.find(c => c.id === post.category) || CATEGORIES[0];
  return (
    <div className="card" style={{ cursor: "pointer", padding: "20px 24px", position: "relative" }} onClick={() => onOpen(post)}>
      {post.pinned && (
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${C.gold},transparent)`, borderRadius: "16px 16px 0 0" }} />
      )}
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
        {/* Upvote */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0 }} onClick={e => { e.stopPropagation(); onUpvote(post.id); }}>
          <button style={{ background: post.upvoted ? C.accentGlow : "rgba(255,255,255,0.04)", border: `1px solid ${post.upvoted ? C.accent : C.border}`, borderRadius: 8, width: 36, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 14, color: post.upvoted ? C.accent : C.textMuted, transition: "all 0.2s" }}>
            ▲
          </button>
          <span style={{ fontSize: 12, fontWeight: 700, color: post.upvoted ? C.accent : C.textMuted }}>{post.upvotes}</span>
        </div>
        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, padding: "3px 9px", borderRadius: 6, background: cat.color + "18", color: cat.color, fontWeight: 700, border: `1px solid ${cat.color}30` }}>
              {cat.icon} {cat.label}
            </span>
            {post.pinned && <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 6, background: C.goldGlow, color: C.gold, fontWeight: 700, border: `1px solid ${C.gold}30` }}>📌 PINNED</span>}
            {post.solved && <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 6, background: C.greenGlow, color: C.green, fontWeight: 700, border: `1px solid ${C.green}30` }}>✅ SOLVED</span>}
          </div>
          <h3 style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.4, marginBottom: 6, color: C.text }}>{post.title}</h3>
          <p style={{ fontSize: 13, color: C.textMuted, lineHeight: 1.6, marginBottom: 10, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {post.body}
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <Avatar initials={post.avatar} size={22} />
              <span style={{ fontSize: 12, color: C.textMuted }}>{post.author}</span>
            </div>
            <span style={{ fontSize: 11, color: C.textFaint }}>{post.time}</span>
            <span style={{ fontSize: 12, color: C.textMuted }}>💬 {post.replies} replies</span>
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {post.tags.slice(0, 2).map(tag => (
                <span key={tag} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 5, background: "rgba(255,255,255,0.05)", color: C.textFaint, border: `1px solid ${C.border}` }}>#{tag}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PostModal({ post, onClose, onUpvote }) {
  const [reply, setReply] = useState("");
  const [replies, setReplies] = useState([
    { id: 1, author: "Alex R.", avatar: "AR", time: "1 hour ago", text: "Great share! I've been doing something similar but hadn't thought to combine it this way. What tool did you use for the scheduling part?" },
    { id: 2, author: "Priya S.", avatar: "PS", time: "30 min ago", text: "This is exactly what I needed. Following for updates!" },
  ]);
  const { user } = useUser();
  const cat = CATEGORIES.find(c => c.id === post.category) || CATEGORIES[0];

  const submitReply = () => {
    if (!reply.trim()) return;
    setReplies([...replies, {
      id: replies.length + 1,
      author: user?.fullName || "Anonymous",
      avatar: (user?.fullName || "AN").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase(),
      time: "Just now",
      text: reply,
    }]);
    setReply("");
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 680, maxHeight: "90vh" }}>
        <div style={{ height: 4, background: `linear-gradient(90deg,${cat.color},${C.accent})`, borderRadius: "20px 20px 0 0", margin: "-36px -36px 24px" }} />

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, padding: "3px 9px", borderRadius: 6, background: cat.color + "18", color: cat.color, fontWeight: 700, border: `1px solid ${cat.color}30` }}>{cat.icon} {cat.label}</span>
            {post.solved && <span style={{ fontSize: 10, padding: "3px 9px", borderRadius: 6, background: C.greenGlow, color: C.green, fontWeight: 700, border: `1px solid ${C.green}30` }}>✅ SOLVED</span>}
          </div>
          <button className="btn-ghost" onClick={onClose} style={{ fontSize: 18 }}>✕</button>
        </div>

        <h2 style={{ fontFamily: F.display, fontSize: 20, fontWeight: 800, marginBottom: 12, lineHeight: 1.3 }}>{post.title}</h2>

        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <Avatar initials={post.avatar} size={32} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{post.author}</div>
            <div style={{ fontSize: 11, color: C.textFaint }}>{post.time}</div>
          </div>
        </div>

        <p style={{ fontSize: 14, color: C.textMuted, lineHeight: 1.8, marginBottom: 16 }}>{post.body}</p>

        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 24, paddingBottom: 20, borderBottom: `1px solid ${C.border}` }}>
          <button onClick={() => onUpvote(post.id)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 8, background: post.upvoted ? C.accentGlow : "rgba(255,255,255,0.04)", border: `1px solid ${post.upvoted ? C.accent : C.border}`, color: post.upvoted ? C.accent : C.textMuted, cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: F.body, transition: "all 0.2s" }}>
            ▲ {post.upvotes} upvotes
          </button>
          <span style={{ fontSize: 13, color: C.textMuted }}>💬 {post.replies + replies.length - 2} replies</span>
          <div style={{ display: "flex", gap: 5 }}>
            {post.tags.map(tag => <span key={tag} style={{ fontSize: 10, padding: "2px 8px", borderRadius: 5, background: "rgba(255,255,255,0.05)", color: C.textFaint, border: `1px solid ${C.border}` }}>#{tag}</span>)}
          </div>
        </div>

        {/* Replies */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 14 }}>
            {replies.length} Replies
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, maxHeight: 260, overflowY: "auto" }}>
            {replies.map(r => (
              <div key={r.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "14px", borderRadius: 10, background: C.accentGlow2, border: `1px solid ${C.border}` }}>
                <Avatar initials={r.avatar} size={28} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{r.author}</span>
                    <span style={{ fontSize: 10, color: C.textFaint }}>{r.time}</span>
                  </div>
                  <p style={{ fontSize: 13, color: C.textMuted, lineHeight: 1.65, margin: 0 }}>{r.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Reply input */}
        <SignedIn>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <Avatar initials={(user?.fullName || "U").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() || "U"} size={32} />
            <div style={{ flex: 1 }}>
              <textarea
                className="input-field"
                rows={3}
                placeholder="Write a reply..."
                value={reply}
                onChange={e => setReply(e.target.value)}
                style={{ resize: "none", marginBottom: 8 }}
                onKeyDown={e => { if (e.key === "Enter" && e.metaKey) submitReply(); }}
              />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 10, color: C.textFaint }}>⌘↵ to submit</span>
                <button className="btn-primary" style={{ padding: "8px 18px", fontSize: 13 }} onClick={submitReply}>Post Reply</button>
              </div>
            </div>
          </div>
        </SignedIn>
        <SignedOut>
          <div style={{ padding: "16px", borderRadius: 10, background: C.accentGlow, border: `1px solid ${C.border}`, textAlign: "center" }}>
            <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 10 }}>Sign in to join the conversation</div>
            <SignInButton mode="modal"><button className="btn-primary" style={{ padding: "9px 22px", fontSize: 13 }}>Sign In to Reply</button></SignInButton>
          </div>
        </SignedOut>
      </div>
    </div>
  );
}

function NewPostModal({ onClose, onSubmit }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("ai");
  const [tags, setTags] = useState("");

  const submit = () => {
    if (!title.trim() || !body.trim()) return;
    onSubmit({ title, body, category, tags: tags.split(",").map(t => t.trim()).filter(Boolean) });
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h2 style={{ fontFamily: F.display, fontSize: 22, fontWeight: 800 }}>Create a Post</h2>
          <button className="btn-ghost" onClick={onClose} style={{ fontSize: 18 }}>✕</button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.textMuted, display: "block", marginBottom: 6 }}>Category</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {CATEGORIES.filter(c => c.id !== "all").map(cat => (
                <button key={cat.id} onClick={() => setCategory(cat.id)}
                  style={{ padding: "6px 12px", borderRadius: 8, border: `1px solid ${category === cat.id ? cat.color : C.border}`, background: category === cat.id ? cat.color + "18" : "transparent", color: category === cat.id ? cat.color : C.textMuted, fontSize: 12, fontWeight: category === cat.id ? 700 : 500, cursor: "pointer", fontFamily: F.body, transition: "all 0.2s" }}>
                  {cat.icon} {cat.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.textMuted, display: "block", marginBottom: 6 }}>Title <span style={{ color: C.red }}>*</span></label>
            <input className="input-field" placeholder="What's your post about?" value={title} onChange={e => setTitle(e.target.value)} />
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.textMuted, display: "block", marginBottom: 6 }}>Body <span style={{ color: C.red }}>*</span></label>
            <textarea className="input-field" rows={5} placeholder="Share your question, insight, or experience..." value={body} onChange={e => setBody(e.target.value)} style={{ resize: "vertical" }} />
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.textMuted, display: "block", marginBottom: 6 }}>Tags <span style={{ color: C.textFaint }}>(comma separated)</span></label>
            <input className="input-field" placeholder="e.g. chatgpt, automation, income" value={tags} onChange={e => setTags(e.target.value)} />
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", paddingTop: 8 }}>
            <button className="btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn-primary" onClick={submit} style={{ padding: "10px 24px" }} disabled={!title.trim() || !body.trim()}>
              Post to Community
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CommunityPage() {
  const [posts, setPosts] = useState(INITIAL_POSTS);
  const [activeCategory, setActiveCategory] = useState("all");
  const [sort, setSort] = useState("hot");
  const [search, setSearch] = useState("");
  const [openPost, setOpenPost] = useState(null);
  const [showNewPost, setShowNewPost] = useState(false);
  const { user } = useUser();

  const handleUpvote = (id) => {
    setPosts(prev => prev.map(p => p.id === id
      ? { ...p, upvotes: p.upvoted ? p.upvotes - 1 : p.upvotes + 1, upvoted: !p.upvoted }
      : p
    ));
    if (openPost?.id === id) {
      setOpenPost(prev => ({ ...prev, upvotes: prev.upvoted ? prev.upvotes - 1 : prev.upvotes + 1, upvoted: !prev.upvoted }));
    }
  };

  const handleNewPost = ({ title, body, category, tags }) => {
    const newPost = {
      id: posts.length + 1, category, title, body,
      author: user?.fullName || "Anonymous",
      avatar: (user?.fullName || "AN").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase(),
      time: "Just now", upvotes: 1, replies: 0, tags, pinned: false, solved: false,
    };
    setPosts([newPost, ...posts]);
  };

  const filtered = posts
    .filter(p => activeCategory === "all" || p.category === activeCategory)
    .filter(p => search === "" || p.title.toLowerCase().includes(search.toLowerCase()) || p.body.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      if (sort === "hot") return b.upvotes - a.upvotes;
      if (sort === "new") return b.id - a.id;
      if (sort === "top") return b.replies - a.replies;
      return 0;
    });

  const totalMembers = 12847;
  const onlineNow = 234;

  return (
    <div className="fade-in" style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 24px 80px" }}>

      {openPost    && <PostModal post={openPost} onClose={() => setOpenPost(null)} onUpvote={handleUpvote} />}
      {showNewPost && (
        <SignedIn>
          <NewPostModal onClose={() => setShowNewPost(false)} onSubmit={handleNewPost} />
        </SignedIn>
      )}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32, flexWrap: "wrap", gap: 16 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 8 }}>SkillBite</div>
          <h1 style={{ fontFamily: F.display, fontSize: "clamp(26px,3.5vw,38px)", fontWeight: 900, marginBottom: 6 }}>Community Forum</h1>
          <p style={{ color: C.textMuted, fontSize: 13 }}>Share wins, ask questions, and learn from 12K+ members</p>
        </div>
        <SignedIn>
          <button className="btn-primary" style={{ padding: "12px 24px", flexShrink: 0 }} onClick={() => setShowNewPost(true)}>
            ✏️ New Post
          </button>
        </SignedIn>
        <SignedOut>
          <SignInButton mode="modal">
            <button className="btn-primary" style={{ padding: "12px 24px", flexShrink: 0 }}>
              Sign In to Post
            </button>
          </SignInButton>
        </SignedOut>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: 28, alignItems: "start" }}>

        {/* Main feed */}
        <div>
          {/* Search + sort */}
          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
            <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
              <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: C.textMuted, fontSize: 14 }}>🔍</span>
              <input className="input-field" placeholder="Search posts..." value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 36 }} />
            </div>
            <div style={{ display: "flex", borderRadius: 10, overflow: "hidden", border: `1px solid ${C.border}` }}>
              {[["hot","🔥 Hot"],["new","✨ New"],["top","💬 Top"]].map(([id, label]) => (
                <button key={id} onClick={() => setSort(id)}
                  style={{ padding: "9px 16px", background: sort === id ? C.accent : "transparent", color: sort === id ? "#00171F" : C.textMuted, border: "none", cursor: "pointer", fontSize: 12, fontWeight: sort === id ? 700 : 500, fontFamily: F.body, transition: "all 0.2s" }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Category pills */}
          <div style={{ display: "flex", gap: 7, marginBottom: 20, flexWrap: "wrap" }}>
            {CATEGORIES.map(cat => (
              <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
                style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 13px", borderRadius: 50, border: `1px solid ${activeCategory === cat.id ? cat.color : C.border}`, background: activeCategory === cat.id ? cat.color + "18" : "transparent", color: activeCategory === cat.id ? cat.color : C.textMuted, fontSize: 12, fontWeight: activeCategory === cat.id ? 700 : 500, cursor: "pointer", fontFamily: F.body, transition: "all 0.2s", whiteSpace: "nowrap" }}>
                {cat.icon} {cat.label}
              </button>
            ))}
          </div>

          {/* Post count */}
          <div style={{ fontSize: 12, color: C.textFaint, marginBottom: 14 }}>
            {filtered.length} post{filtered.length !== 1 ? "s" : ""} {activeCategory !== "all" ? `in ${CATEGORIES.find(c => c.id === activeCategory)?.label}` : ""}
          </div>

          {/* Posts */}
          {filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: C.textMuted }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>🔍</div>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>No posts found</div>
              <div style={{ fontSize: 12 }}>Try a different search or category</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {filtered.map(post => (
                <PostCard key={post.id} post={post} onOpen={setOpenPost} onUpvote={handleUpvote} />
              ))}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16, position: "sticky", top: 80 }}>

          {/* Community stats */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: "20px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 14 }}>Community</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              {[
                { n: totalMembers.toLocaleString(), l: "Members", color: C.accent },
                { n: onlineNow, l: "Online now", color: C.green },
                { n: posts.length, l: "Total posts", color: C.gold },
                { n: "24hr", l: "Response time", color: C.purple },
              ].map(s => (
                <div key={s.l} style={{ textAlign: "center", padding: "10px 6px", background: C.accentGlow2, borderRadius: 8 }}>
                  <div style={{ fontSize: 18, fontWeight: 900, color: s.color, fontFamily: F.display }}>{s.n}</div>
                  <div style={{ fontSize: 10, color: C.textMuted }}>{s.l}</div>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 8, background: C.greenGlow, border: `1px solid ${C.green}30` }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: C.green, animation: "pulse 2s infinite" }} />
              <span style={{ fontSize: 12, color: C.green, fontWeight: 600 }}>{onlineNow} members online</span>
            </div>
          </div>

          {/* Top contributors */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: "20px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.gold, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 14 }}>🏆 Top Contributors</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[
                { name: "Sarah K.", avatar: "SK", posts: 47, badge: "🥇" },
                { name: "Marcus L.", avatar: "ML", posts: 39, badge: "🥈" },
                { name: "Lisa P.", avatar: "LP", posts: 31, badge: "🥉" },
                { name: "Emeka D.", avatar: "ED", posts: 28, badge: "⭐" },
                { name: "Maya C.", avatar: "MC", posts: 22, badge: "⭐" },
              ].map((u, i) => (
                <div key={u.name} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 14, minWidth: 20 }}>{u.badge}</span>
                  <Avatar initials={u.avatar} size={28} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{u.name}</div>
                    <div style={{ fontSize: 10, color: C.textFaint }}>{u.posts} posts</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Community guidelines */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: "20px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 12 }}>Community Rules</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {[
                "Be respectful and kind",
                "Share practical value",
                "No spam or self-promotion",
                "Mark solved questions",
                "Give credit to sources",
              ].map((rule, i) => (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <span style={{ color: C.accent, fontSize: 12, marginTop: 1 }}>→</span>
                  <span style={{ fontSize: 12, color: C.textMuted, lineHeight: 1.5 }}>{rule}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Trending tags */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: "20px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 12 }}>Trending Tags</div>
            <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
              {["chatgpt","automation","whatsapp","income","nigeria","tiktok","agents","nocode","templates","india"].map(tag => (
                <span key={tag} onClick={() => setSearch(tag)} style={{ fontSize: 11, padding: "4px 9px", borderRadius: 6, background: C.accentGlow2, color: C.accent, border: `1px solid ${C.border}`, cursor: "pointer", transition: "all 0.2s" }}
                  onMouseEnter={e => e.target.style.background = C.accentGlow}
                  onMouseLeave={e => e.target.style.background = C.accentGlow2}>
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
